import { type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Render } from '@/components/render-if'
import { Button, NumberInputField } from '@/components/ui'
import { AutoCompleteField } from '@/components/ui/auto-complete'
import { showToast } from '@/infra/sonner'

type TImobiliariaFieldConfig<TFieldValues extends FieldValues, TOption extends { idExterno: string }> = {
  name: FieldPath<TFieldValues>
  data: TOption[]
  labelKey: keyof TOption
  label: string
  onSearch: (query: string) => void
  onSelect: (value: TOption | TOption[keyof TOption]) => void
  isLoading?: boolean
  isError?: boolean
  retry?: VoidFunction
  isLoadingMore?: boolean
  onEndReach?: VoidFunction
  placeholder?: string
  emptyMessage?: string
}

type TParticipacaoFieldConfig<TFieldValues extends FieldValues> = {
  name: FieldPath<TFieldValues>
  label: string
  formatOptions: Intl.NumberFormatOptions
  maxValue?: number
}

type TCardImobiliariaCorretorBaseProps<TFieldValues extends FieldValues, TOption extends { idExterno: string }> = {
  control: Control<TFieldValues>
  onExcluirParticipacao: VoidFunction
  isDeleteEnabled?: boolean
  imobiliaria: TImobiliariaFieldConfig<TFieldValues, TOption>
  participacao: TParticipacaoFieldConfig<TFieldValues>
}

export const CardImobiliariaCorretorBase = <TFieldValues extends FieldValues, TOption extends { idExterno: string }>({
  control,
  onExcluirParticipacao,
  isDeleteEnabled = true,
  imobiliaria,
  participacao,
}: TCardImobiliariaCorretorBaseProps<TFieldValues, TOption>) => {
  const { t } = useTranslation('proposta')

  const notificarValorZerado = (valor: number) => {
    if (valor === 0) {
      showToast({
        message: t('detalhes_proposta.imobiliaria.errors.toast.valor_zerado'),
        variant: 'error',
        closeButton: true,
      })
    }
  }

  return (
    <div className="flex w-full flex-col gap-4 md:flex-row md:flex-wrap md:items-start md:gap-y-1">
      <AutoCompleteField
        control={control}
        name={imobiliaria.name}
        valueKey="idExterno"
        labelKey={imobiliaria.labelKey}
        data={imobiliaria.data}
        label={imobiliaria.label}
        onSearch={imobiliaria.onSearch}
        onSelect={imobiliaria.onSelect}
        isLoading={imobiliaria.isLoading}
        isError={imobiliaria.isError}
        retry={imobiliaria.retry}
        isLoadingMore={imobiliaria.isLoadingMore}
        onEndReach={imobiliaria.onEndReach}
        placeholder={imobiliaria.placeholder}
        emptyMessage={imobiliaria.emptyMessage}
        className="w-full truncate md:w-50 xl:w-96"
      />
      <NumberInputField
        control={control}
        name={participacao.name}
        label={participacao.label}
        className="md:max-w-50 xl:w-96"
        numberFieldProps={{ formatOptions: participacao.formatOptions, maxValue: participacao.maxValue }}
        onChange={(event) => notificarValorZerado(Number(event.target.value))}
      />
      <Render.If condition={isDeleteEnabled}>
        {() => (
          <Button
            type="button"
            variant="link"
            className="mb-3 h-10 text-red-800 md:mt-6"
            onClick={onExcluirParticipacao}
          >
            {t('detalhes_proposta.imobiliaria.botoes.excluir')}
          </Button>
        )}
      </Render.If>
    </div>
  )
}
